/**
 * UI Slice
 * Manages UI state (widget, animations, visibility flags)
 */

import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { UIState } from '../types';
import { signIn } from './auth-slice';
import { checkValidEmail, updateUserDetails } from './user-slice';

// Initial State
export const uiInitialState: UIState = {
    showUserDetails: false,
    isWidgetLoaded: false,
    showResetPassword: false,
    isAnimating: false,
};

// UI Slice
export const uiSlice = createSlice({
    name: 'ui',
    initialState: uiInitialState,
    reducers: {
        setShowUserDetails(state, action: PayloadAction<boolean>) {
            state.showUserDetails = action.payload;
        },
        setWidgetLoaded(state, action: PayloadAction<boolean>) {
            state.isWidgetLoaded = action.payload;
        },
        setShowResetPassword(state, action: PayloadAction<boolean>) {
            state.showResetPassword = action.payload;
        },
        setIsAnimating(state, action: PayloadAction<boolean>) {
            state.isAnimating = action.payload;
        },
        resetUI(state) {
            Object.assign(state, uiInitialState);
        },
    },
    extraReducers: (builder) => {
        // Handle checkValidEmail - show user details form if needed
        builder.addCase(checkValidEmail.fulfilled, (state, action) => {
            if (action.payload.show_storefront_user_details) {
                state.showUserDetails = true;
            }
        });

        // Handle signIn - set showUserDetails from enriched login response
        builder.addCase(signIn.fulfilled, (state, action) => {
            if (action.payload.email_status?.show_storefront_user_details) {
                state.showUserDetails = true;
            }
        });

        // Handle updateUserDetails - hide user details form
        builder.addCase(updateUserDetails.fulfilled, (state) => {
            state.showUserDetails = false;
        });
    },
});

// Export UI actions
export const { setShowUserDetails, setWidgetLoaded, setShowResetPassword, setIsAnimating, resetUI } = uiSlice.actions;

// UI Selectors
type UIRootState = { ui: UIState };

export const selectUI = (state: UIRootState): UIState => state.ui;
export const selectShowUserDetailsForm = createSelector(selectUI, (ui) => ui.showUserDetails);
export const selectIsWidgetLoaded = createSelector(selectUI, (ui) => ui.isWidgetLoaded);
export const selectShowResetPassword = createSelector(selectUI, (ui) => ui.showResetPassword);
export const selectIsAnimating = createSelector(selectUI, (ui) => ui.isAnimating);

export const uiReducer = uiSlice.reducer;
